import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  LinearProgress,
  Alert,
  CircularProgress,
  Button,
  Avatar,
  Paper,
  Stack,
  Divider,
} from '@mui/material';
import {
  Security as SecurityIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  Upgrade as UpgradeIcon,
  CheckCircle as CheckCircleIcon,
  Speed as SpeedIcon,
  // Assessment as AssessmentIcon,
  Description as DescriptionIcon,
  VpnKey as VpnKeyIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiClient, DashboardAnalytics, BillingMeResponse, Scan } from '../../services/api';
import { format, subDays } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';

// Dummy GitHub releases URL – replace with actual CLI binary releases when available
const GITHUB_BINARY_RELEASES_URL = 'https://github.com/amulyakashyap09/baastream-public/tree/master/assets';

interface QuickStatProps {
  icon: React.ReactElement;
  title: string;
  value: string | number;
  color: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  subtitle?: string;
}

function QuickStat({ icon, title, value, color, subtitle }: QuickStatProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 2,
        borderRadius: 2,
        bgcolor: '#ffffff',
        border: '1px solid',
        borderColor: 'divider',
        borderLeft: '4px solid',
        borderLeftColor: color === 'primary' ? 'primary.main' : `${color}.main`,
      }}
    >
      <Avatar sx={{ bgcolor: color === 'primary' ? 'primary.main' : `${color}.main`, width: 48, height: 48, color: '#fff' }}>
        {icon}
      </Avatar>
      <Box flex={1}>
        <Typography variant="h5" fontWeight="bold">
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export function DashboardOverview() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [analytics, setAnalytics] = useState<DashboardAnalytics | null>(null);
  const [billing, setBilling] = useState<BillingMeResponse | null>(null);
  const [ruleCoverage, setRuleCoverage] = useState<{
    tierId: string;
    tierName: string;
    count: number | string;
    type: string;
    categories: string[];
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalScansFromApi, setTotalScansFromApi] = useState<number | null>(null);
  const [recentScansFromApi, setRecentScansFromApi] = useState<Scan[] | null>(null);
  const [issuesFromScans, setIssuesFromScans] = useState<{
    total: number;
    critical: number;
    high: number;
    medium: number;
    low: number;
  } | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch billing/usage data
      const billingData = await apiClient.getMyBilling();
      setBilling(billingData);

      // Load rule coverage for the user's plan from pricing.json (source of truth for rule categories/count).
      try {
        const planId = billingData?.plan?.id ? String(billingData.plan.id).toLowerCase() : null;
        if (planId) {
          const pricingRes = await fetch('/pricing.json');
          if (pricingRes.ok) {
            const pricingData = await pricingRes.json();
            const tiers = Array.isArray(pricingData?.tiers) ? pricingData.tiers : [];
            const tier = tiers.find((t: any) => String(t?.id || '').toLowerCase() === planId);
            const sr = tier?.limits?.securityRules;
            if (tier && sr) {
              setRuleCoverage({
                tierId: String(tier.id),
                tierName: String(tier.name || billingData.plan?.name || tier.id),
                count: sr.count ?? 'N/A',
                type: String(sr.type || 'N/A'),
                categories: Array.isArray(sr.categories) ? sr.categories.map((c: any) => String(c)) : [],
              });
            } else {
              setRuleCoverage(null);
            }
          } else {
            setRuleCoverage(null);
          }
        } else {
          setRuleCoverage(null);
        }
      } catch {
        setRuleCoverage(null);
      }

      // Fetch recent analytics (last 30 days for better coverage)
      const endDate = new Date();
      const startDate = subDays(endDate, 30);
      
      const analyticsData = await apiClient.getDashboardAnalytics({
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd'),
      });
      
      setAnalytics(analyticsData);

      // Fallback: If analytics shows 0 scans or no data, fetch directly from scans API
      const needsFallback = analyticsData.summary.totalScans === 0 || 
                           analyticsData.summary.totalIssues === 0 ||
                           !analyticsData.recentScans || 
                           analyticsData.recentScans.length === 0;

      if (needsFallback) {
        try {
          const scansResponse = await apiClient.getScans({ limit: 10, sortBy: 'createdAt', sortOrder: 'desc' });
          if (scansResponse.meta?.total) {
            setTotalScansFromApi(scansResponse.meta.total);
          }
          if (scansResponse.scans && scansResponse.scans.length > 0) {
            setRecentScansFromApi(scansResponse.scans);

            // Calculate total issues and severity breakdown from actual scans
            let totalIssues = 0;
            let critical = 0;
            let high = 0;
            let medium = 0;
            let low = 0;

            scansResponse.scans.forEach(scan => {
              if (scan.summary) {
                totalIssues += scan.summary.failed || 0;
                critical += scan.summary.failedBySeverity?.critical || 0;
                high += scan.summary.failedBySeverity?.high || 0;
                medium += scan.summary.failedBySeverity?.medium || 0;
                low += scan.summary.failedBySeverity?.low || 0;
              }
            });

            setIssuesFromScans({
              total: totalIssues,
              critical,
              high,
              medium,
              low,
            });
          }
        } catch (scansErr) {
          console.warn('Could not fetch scans:', scansErr);
        }
      }
    } catch (err: any) {
      console.error('Error fetching dashboard data:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load dashboard data';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button 
          variant="contained" 
          onClick={fetchData}
          disabled={loading}
        >
          {loading ? 'Retrying...' : 'Retry'}
        </Button>
      </Box>
    );
  }

  // Check plan status
  const isFreePlan = billing?.plan?.id === 'free' ||
                     billing?.plan?.name?.toLowerCase().includes('free') ||
                     billing?.plan?.priceMonthlyUsd === 0;

  const usagePercentage = billing?.usage ? 
    (billing.usage.usedScans / billing.usage.allowedScans) * 100 : 0;
  
  const scansRemaining = billing?.usage?.remainingScans ?? 0;
  const isNearLimit = usagePercentage >= 80;

  // Calculate overall compliance health - use fallback from scans if analytics is empty
  const criticalIssues = issuesFromScans ? issuesFromScans.critical : (analytics?.severityBreakdown.critical ?? 0);
  const highIssues = issuesFromScans ? issuesFromScans.high : (analytics?.severityBreakdown.high ?? 0);
  const totalIssues = issuesFromScans ? issuesFromScans.total : (analytics?.summary.totalIssues ?? 0);
  
  let complianceHealth = 'Good';
  let complianceColor: 'success' | 'warning' | 'error' = 'success';
  
  if (criticalIssues > 0) {
    complianceHealth = 'Critical';
    complianceColor = 'error';
  } else if (highIssues > 5 || totalIssues > 20) {
    complianceHealth = 'Needs Attention';
    complianceColor = 'warning';
  } else if (totalIssues > 0) {
    complianceHealth = 'Minor Issues';
    complianceColor = 'warning';
  }

  return (
    <Box sx={{ bgcolor: '#ffffff', minHeight: '100%' }}>
      {/* First Month Free Banner */}
      {isFreePlan && (
        <Box
          sx={{
            bgcolor: '#FF7518',
            borderRadius: 2,
            px: 3,
            py: 1.5,
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600 }}>
            🎉 First month free on any paid plan — upgrade today and get your first month on us.
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => navigate('/pricing')}
            sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.6)', fontWeight: 700, whiteSpace: 'nowrap', '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' } }}
          >
            Upgrade now
          </Button>
        </Box>
      )}
      {/* Welcome Header */}
      <Box sx={{ mb: 4, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h4" component="h3" fontWeight="400" gutterBottom>
          Welcome, {currentUser?.displayName || currentUser?.email?.split('@')[0] || 'User'}!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Here's your compliance overview for the last 30 days
        </Typography>
      </Box>

      {/* Free Plan Alert */}
      {isFreePlan && (
        <Alert 
          severity="info" 
          sx={{ mb: 3 }}
          action={
            <Button 
              color="inherit" 
              size="small" 
              startIcon={<UpgradeIcon />}
              onClick={() => navigate('/pricing')}
            >
              Upgrade
            </Button>
          }
        >
          <Typography variant="body2" fontWeight="bold">
            You're on the Free Plan
          </Typography>
          <Typography variant="body2">
            {billing?.usage && `${scansRemaining} of ${billing.usage.allowedScans} scans remaining this month. `}
            Upgrade for more scans and advanced features.
          </Typography>
        </Alert>
      )}

      {/* Usage Warning */}
      {isNearLimit && !isFreePlan && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight="bold">
            Approaching Scan Limit
          </Typography>
          <Typography variant="body2">
            You've used {billing?.usage?.usedScans} of {billing?.usage?.allowedScans} scans this month ({Math.round(usagePercentage)}%).
          </Typography>
        </Alert>
      )}

      {/* Critical Issues Alert */}
      {criticalIssues > 0 && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight="bold">
            ⚠️ {criticalIssues} Critical Security Issues Found
          </Typography>
          <Typography variant="body2">
            Immediate action required. Review your scans to address critical vulnerabilities.
          </Typography>
          <Button 
            size="small" 
            sx={{ mt: 1 }} 
            variant="outlined" 
            color="error"
            onClick={() => navigate('/scans')}
          >
            View Scans
          </Button>
        </Alert>
      )}

      {/* Usage Summary Card - two-tone primary to white */}
      <Card sx={{ mb: 3, background: 'linear-gradient(85deg,  #FF7518 0%,  #FFD1B3 45%,  #FFD1B3 55%,  #FF7518 100%)', color: 'white', border: 'none', boxShadow: '0 2px 12px rgba(97, 58, 11, 0.25)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="h6" gutterBottom sx={{ color: 'white', fontWeight: 600 }}>
                Monthly Usage
              </Typography>
              <Typography variant="h3" fontWeight="bold" sx={{ color: 'white' }}>
                {billing?.usage?.usedScans ?? 0} / {billing?.usage?.allowedScans ?? 0}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.95, color: 'white' }}>
                Scans used this month
              </Typography>
            </Box>
            <Chip 
              label={billing?.plan?.name || 'Free'} 
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.25)', 
                color: 'white',
                fontWeight: 'bold',
                border: '1px solid rgba(255,255,255,0.4)',
              }} 
            />
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={Math.min(usagePercentage, 100)} 
            sx={{ 
              height: 8, 
              borderRadius: 4,
              bgcolor: 'rgba(255,255,255,0.75)',
              '& .MuiLinearProgress-bar': {
                bgcolor: usagePercentage >= 100 ? '#d32f2f' : 
                         usagePercentage >= 80 ? '#ed6c02' : '#2e7d32',
              },
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
            <Typography variant="body2" sx={{ color: 'white', opacity: 0.95 }}>
              {scansRemaining} scans remaining
            </Typography>
            <Typography variant="body2" sx={{ color: 'white', opacity: 0.95 }}>
              Resets: {billing?.usage?.periodEnd ? format(new Date(billing.usage.periodEnd), 'MMM dd') : 'N/A'}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Rule Coverage */}
      {ruleCoverage && (
        <Card sx={{ mb: 3, bgcolor: '#ffffff' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Rule coverage in your plan
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {ruleCoverage.tierName}: {ruleCoverage.count} {ruleCoverage.type} rules
                </Typography>
                {ruleCoverage.categories.length > 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Categories: {ruleCoverage.categories.join(', ')}
                  </Typography>
                )}
              </Box>
              <Button variant="outlined" size="small" onClick={() => navigate('/pricing')} sx={{ borderColor: '#FF7518', color: '#FF7518' }}>
                View plans
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Quick Stats Grid */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
          <QuickStat
            icon={<SecurityIcon />}
            title="Total Scans"
            value={totalScansFromApi !== null ? totalScansFromApi : (analytics?.summary.totalScans ?? 0)}
            color="primary"
            subtitle={totalScansFromApi !== null ? "All time" : "Last 30 days"}
          />
        </Box>
        <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
          <QuickStat
            icon={<WarningIcon />}
            title="Issues Found"
            value={totalIssues}
            color="warning"
            subtitle={`${criticalIssues} critical, ${highIssues} high`}
          />
        </Box>
        <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
          <QuickStat
            icon={<SpeedIcon />}
            title="Compliance Health"
            value={complianceHealth}
            color={complianceColor}
            subtitle="Overall status"
          />
        </Box>
      </Box>

      {/* Quick Actions */}
      <Card sx={{ mb: 3, bgcolor: '#ffffff', borderColor: 'divider' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 600 }}>
            Quick Actions
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<VpnKeyIcon />}
              onClick={() => navigate('/token')}
              size="large"
              sx={{ bgcolor: '#FF7518', '&:hover': { bgcolor: '#4a2d08' } }}
            >
              Get CLI Token
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => window.open(GITHUB_BINARY_RELEASES_URL, '_blank', 'noopener,noreferrer')}
              size="large"
              sx={{ borderColor: '#FF7518', color: '#FF7518', '&:hover': { borderColor: '#4a2d08', bgcolor: 'rgba(97, 58, 11, 0.08)' } }}
            >
              Download Binary
            </Button>
            <Button
              variant="outlined"
              startIcon={<SecurityIcon />}
              onClick={() => navigate('/scans')}
              size="large"
              sx={{ borderColor: 'divider', color: 'text.primary', '&:hover': { borderColor: '#FF7518', color: '#FF7518', bgcolor: 'rgba(97, 58, 11, 0.06)' } }}
            >
              View All Scans
            </Button>
            {/* <Button
              variant="outlined"
              startIcon={<AssessmentIcon />}
              onClick={() => navigate('/analytics')}
              size="large"
            >
              Advanced Analytics
            </Button> */}
            {isFreePlan && (
              <Button
                variant="contained"
                startIcon={<UpgradeIcon />}
                onClick={() => navigate('/pricing')}
                size="large"
                sx={{ bgcolor: '#FF7518', '&:hover': { bgcolor: '#4a2d08' } }}
              >
                Upgrade Plan
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Two Column Layout */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {/* Recent Activity */}
        <Box sx={{ flex: '1 1 400px', minWidth: 400 }}>
          <Card sx={{ bgcolor: '#ffffff' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Recent Activity
              </Typography>
              {!recentScansFromApi && (!analytics?.recentScans || analytics.recentScans.length === 0) ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <SecurityIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography color="text.secondary" gutterBottom>
                    No scans yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Get started by running your first security scan
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<VpnKeyIcon />}
                    onClick={() => navigate('/token')}
                    sx={{ bgcolor: '#FF7518', '&:hover': { bgcolor: '#4a2d08' } }}
                  >
                    Get CLI Token
                  </Button>
                </Box>
              ) : (
                <Stack spacing={2}>
                  {(recentScansFromApi || analytics?.recentScans || []).slice(0, 5).map((scan: any) => {
                    // Handle both analytics format and scans API format
                    const isAnalyticsScan = 'scanType' in scan;
                    const displayName = isAnalyticsScan ? (scan.scanType || 'Security Scan') : (scan.metadata?.scanType || 'Security Scan');
                    const timestamp = isAnalyticsScan ? scan.timestamp : scan.createdAt;
                    const riskScore = scan.riskScore || 0;
                    const issuesCount = isAnalyticsScan ? scan.totalIssues : scan.complianceSummary?.totalIssues || 0;
                    const scanKey = isAnalyticsScan ? scan.scanId : scan.id;

                    return (
                      <Paper
                        key={scanKey}
                        elevation={0}
                        sx={{ 
                          p: 2, 
                          border: 1, 
                          borderColor: 'divider',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                        onClick={() => navigate('/scans')}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {displayName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {format(new Date(timestamp), 'MMM dd, yyyy HH:mm')}
                            </Typography>
                          </Box>
                          <Chip
                            size="small"
                            label={`Risk: ${riskScore}%`}
                            color={
                              riskScore > 30 ? 'error' :
                              riskScore > 5 ? 'warning' : 'success'
                            }
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {issuesCount} issues found
                        </Typography>
                      </Paper>
                    );
                  })}
                  <Button
                    fullWidth
                    variant="text"
                    onClick={() => navigate('/scans')}
                  >
                    View All Scans
                  </Button>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Compliance Summary */}
        <Box sx={{ flex: '1 1 400px', minWidth: 400 }}>
          <Card sx={{ bgcolor: '#ffffff' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Compliance Status
              </Typography>
              {(analytics?.complianceStatus && Object.keys(analytics.complianceStatus).length > 0) || (recentScansFromApi && recentScansFromApi.length > 0) ? (
                <Stack spacing={2}>
                  {(() => {
                    // Use analytics compliance status if available, otherwise calculate from scans
                    const complianceData = analytics?.complianceStatus && Object.keys(analytics.complianceStatus).length > 0
                      ? Object.entries(analytics.complianceStatus)
                      : recentScansFromApi
                      ? (() => {
                          // Calculate compliance from scans
                          const frameworkMap: Record<string, { totalIssues: number; criticalIssues: number; status: string }> = {};
                          recentScansFromApi.forEach(scan => {
                            if (scan.summary?.byFramework) {
                              Object.entries(scan.summary.byFramework).forEach(([fw, count]) => {
                                if (!frameworkMap[fw]) {
                                  frameworkMap[fw] = { totalIssues: 0, criticalIssues: 0, status: 'compliant' };
                                }
                                frameworkMap[fw].totalIssues += count as number;
                              });
                            }
                            // Count critical issues by framework
                            if (scan.results) {
                              scan.results.forEach(result => {
                                if (result.severity === 'critical' && frameworkMap[result.framework]) {
                                  frameworkMap[result.framework].criticalIssues += 1;
                                }
                              });
                            }
                          });
                          // Determine status for each framework
                          Object.keys(frameworkMap).forEach(fw => {
                            const data = frameworkMap[fw];
                            if (data.criticalIssues > 0) {
                              data.status = 'non-compliant';
                            } else if (data.totalIssues > 5) {
                              data.status = 'needs-attention';
                            } else if (data.totalIssues > 0) {
                              data.status = 'compliant';
                            }
                          });
                          return Object.entries(frameworkMap);
                        })()
                      : [];

                    return complianceData.slice(0, 4).map(([framework, status]: [string, any]) => (
                      <Box key={framework}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="body2" fontWeight="bold">
                            {framework}
                          </Typography>
                          <Chip
                            size="small"
                            icon={status.status === 'compliant' ? <CheckCircleIcon /> : <WarningIcon />}
                            label={status.status === 'compliant' ? 'Compliant' : 
                                   status.status === 'needs-attention' ? 'Needs Attention' : 'Issues'}
                            color={
                              status.status === 'compliant' ? 'success' :
                              status.status === 'needs-attention' ? 'warning' : 'error'
                            }
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {status.totalIssues} issues
                          {status.criticalIssues > 0 && ` (${status.criticalIssues} critical)`}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={Math.max(0, 100 - (status.totalIssues * 2))}
                          sx={{ mt: 1 }}
                          color={
                            status.status === 'compliant' ? 'success' :
                            status.status === 'needs-attention' ? 'warning' : 'error'
                          }
                        />
                      </Box>
                    ));
                  })()}
                  <Divider />
                  <Button
                    fullWidth
                    variant="text"
                    startIcon={<TrendingUpIcon />}
                    onClick={() => navigate('/analytics')}
                  >
                    View Detailed Analytics
                  </Button>
                </Stack>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <DescriptionIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography color="text.secondary">
                    No compliance data yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Run scans to see compliance status
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
}
